// Types
export interface Task {
  id: string;
  title: string;
  description?: string;
  stars: number;
  completed: boolean;
  daily: boolean;
  lastCompleted?: string;
  completedDate?: string; // ISO timestamp when task was completed
  order?: number;
  resetInterval?: number; // in minutes
  lastReset?: string; // ISO timestamp
  completionCount?: number; // Track how many times task was completed
  // Money reward properties
  moneyReward?: number; // Amount of money reward for completing this task
  currency?: string; // Currency symbol (e.g., '$', '€')
  // Notification properties
  reminderEnabled?: boolean; // Whether reminders are enabled for this task
  reminderTime?: string; // Time for daily reminder (HH:MM format)
  notificationId?: number; // Unique ID for scheduled notifications
}

export interface Reward {
  id: string;
  title: string;
  description?: string;
  cost: number;
  claimed: boolean;
  claimedOn?: string; // ISO timestamp when the reward was claimed
  imageUrl?: string;
  type?: 'fixed' | 'calculated'; // Type of reward: fixed cost or calculated based on stars
  rate?: number; // For calculated rewards: monetary value per threshold (e.g., 10 for $10)
  threshold?: number; // For calculated rewards: star threshold (e.g., 50 stars)
  currency?: string; // Currency symbol (e.g., '$', '€')
  daily?: boolean; // Whether this reward resets daily at midnight
  lastResetDate?: string; // ISO date string of last reset (YYYY-MM-DD)
}

export interface Achievement {
  id: string;
  title: string;
  description?: string;
  icon?: string; // Keep icon for achievements that might need custom icons
  requiredStars?: number;
  unlocked: boolean;
  date?: string; // ISO timestamp when the achievement was unlocked
  linkedTaskId?: string; // ID of the linked task (if this achievement is task-specific)
  requiredCompletions?: number; // Number of times task must be completed
  secret?: boolean; // Whether this is a secret trophy that shows a hidden icon
  order?: number; // Add order property for manual sorting
}

// In src/utils/storage.ts
export type ThemeName =
  | 'robots'
  | 'unicorn'
  | 'space'
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'volcano'
  | 'trucks'
  | 'ninja'
  | 'spongebob'
  | 'dinosaurs'
  | 'candy';

// Star history tracking
export interface StarHistoryEntry {
  date: string; // ISO date string (YYYY-MM-DD)
  stars: number;
  totalStarsEarned?: number; // Track total stars earned to date
  maxPossibleStars?: number; // maximum stars available that day
  tasksCompleted?: number; // number of tasks completed
  totalTasks?: number; // total tasks available that day
  isPerfectDay?: boolean; // whether all available stars were earned
}

// Wallet settings and data
export interface WalletSettings {
  enabled: boolean;
  conversionRate: number; // How many stars = 1 unit of currency (e.g., 10 stars = $1)
}

export interface WalletData {
  currentBalance: number; // Current wallet balance in currency units
  lifetimeEarned: number; // Total amount earned throughout all time
}

export interface WishItem {
  id: string; // unique identifier
  title: string; // item name
  description?: string; // optional description
  cost: number; // cost in stars or money, depending on wallet settings
  imageUrl?: string; // optional image URL
  notes?: string; // optional personal notes
  purchaseLink?: string; // optional URL where the item can be purchased
  dateAdded: string; // ISO timestamp when item was added
  achieved: boolean; // whether the item has been obtained
  achievedDate?: string; // ISO timestamp when item was achieved
  progress: number; // progress towards achieving this item (0-100)
}

// Money tracking entry for income or expense
export interface MoneyTrackingEntry {
  id: string; // unique identifier
  title: string; // description of the money entry
  amount: number; // money amount (positive for income, negative for expenses/deductions)
  dateAdded: string; // ISO timestamp when entry was added
}

// Tab visibility settings
export interface TabSettings {
  tasksEnabled: boolean;
  rewardsEnabled: boolean;
  trophiesEnabled: boolean;
  wishListEnabled: boolean;
  walletEnabled: boolean; // Independent wallet tab visibility
  moneyTrackingEnabled?: boolean; // Money tracking tab visibility (deprecated - merged into wallet)
  allowAddMoney?: boolean; // Allow adding money sources from main page (not just edit mode)
}

// Family sharing types
export type FamilyRole = 'owner' | 'parent' | 'child';

export interface FamilyMember {
  userId: string;
  role: FamilyRole;
  displayName: string;
  email: string;
  photoURL?: string;
  joinedAt: string; // ISO timestamp
}

export interface FamilyInvite {
  code: string; // Unique invite code
  createdBy: string; // userId who created the invite
  createdAt: string; // ISO timestamp
  usedBy?: string; // userId who used the invite
  usedAt?: string; // ISO timestamp when invite was used
  status: 'active' | 'used' | 'revoked';
}

export interface FamilyMetadata {
  id: string; // familyId
  owner: string; // userId of the host/owner
  createdAt: string; // ISO timestamp
  name?: string; // Optional family name
}

export interface ActivityLogEntry {
  id: string;
  userId: string;
  userName: string;
  action: 'task_completed' | 'task_uncompleted' | 'reward_claimed' | 'task_added' | 'task_edited' | 'reward_added' | 'reward_edited' | 'stars_adjusted';
  timestamp: string; // ISO timestamp
  details: {
    taskTitle?: string;
    rewardTitle?: string;
    starsEarned?: number;
    starsAdjusted?: number;
    [key: string]: unknown;
  };
}

export interface AppData {
  tasks: Task[];
  rewards: Reward[];
  stars: number;
  theme: ThemeName;
  streakDays: number;
  achievements: Achievement[];
  lastLogin?: string;
  lastAccessDate?: string; // YYYY-MM-DD format for unified day tracking
  lastSync?: string; // Add lastSync property to fix the TypeScript error
  starHistory?: StarHistoryEntry[]; // Track star progress over time
  totalStarsEarned?: number; // Track total stars earned throughout all time
  walletSettings?: WalletSettings; // Wallet configuration
  walletData?: WalletData; // User wallet data
  bottomNavigation?: boolean; // Enable bottom tab navigation instead of top tabs
  wishListEnabled?: boolean; // Enable wish list feature
  wishList?: WishItem[]; // User wish list items
  tabSettings?: TabSettings; // Individual tab visibility settings
  moneyTracking?: MoneyTrackingEntry[]; // Money tracking entries from external sources
  // Family sharing data
  familyId?: string; // ID of the family this data belongs to
  familyMetadata?: FamilyMetadata; // Metadata about the family
  familyMembers?: { [userId: string]: FamilyMember }; // Map of family members
  familyInvites?: { [code: string]: FamilyInvite }; // Map of invite codes
  activityLog?: ActivityLogEntry[]; // Activity history (future phase)
  lastLogCleanup?: string; // ISO timestamp of last activity log cleanup
}

// Default achievements
const defaultAchievements: Achievement[] = [
  {
    id: '1',
    title: '⭐ Star Chores',
    description: 'Collect your first 10 stars',
    requiredStars: 10,
    unlocked: false
  },
  {
    id: '2',
    title: '🌟 Rising Star',
    description: 'Collect 25 stars',
    requiredStars: 25,
    unlocked: false
  },
  {
    id: '3',
    title: '✨ Star Gazer',
    description: 'Collect 50 stars',
    requiredStars: 50,
    unlocked: false
  },
  {
    id: '4',
    title: '🌌 Galaxy Explorer',
    description: 'Collect 100 stars',
    requiredStars: 100,
    unlocked: false
  },
  {
    id: '5',
    title: '💫 Supernova',
    description: 'Collect 200 stars',
    requiredStars: 200,
    unlocked: false
  },
  {
    id: '6',
    title: '🌠 Cosmic Legend',
    description: 'Collect 500 stars',
    requiredStars: 500,
    unlocked: false
  },
  {
    id: '7',
    title: '📅 Daily Champion',
    description: 'Complete all daily tasks for a week',
    requiredStars: 75,
    unlocked: false
  },
  {
    id: '8',
    title: '📝 Task Master',
    description: 'Complete 50 tasks',
    requiredStars: 150,
    unlocked: false
  },
  {
    id: '9',
    title: '🐦 Early Bird',
    description: 'Complete tasks before noon',
    requiredStars: 60,
    unlocked: false
  },
  {
    id: '10',
    title: '🏆 Weekend Warrior',
    description: 'Complete tasks on weekends',
    requiredStars: 80,
    unlocked: false
  },
  {
    id: '11',
    title: '👑 Consistency King',
    description: 'Maintain a 7-day streak',
    requiredStars: 120,
    unlocked: false
  },
  {
    id: '12',
    title: '🎯 Goal Crusher',
    description: 'Claim 10 rewards',
    requiredStars: 180,
    unlocked: false
  },
  {
    id: '13',
    title: '🪥 Dental Champ',
    description: 'Brush teeth 10 times',
    requiredStars: 0,
    unlocked: false,
    linkedTaskId: '1', // ID of "Brush teeth morning" task
    requiredCompletions: 10
  }
];

// Default tasks
const defaultTasks: Task[] = [
  {
    id: '1',
    title: '🪥 Brush teeth morning',
    stars: 1,
    completed: false,
    daily: true,
    order: 0
  },
  {
    id: '2',
    title: '🏫 Go to school',
    stars: 2,
    completed: false,
    daily: true,
    order: 1
  },
  {
    id: '3',
    title: '🪥 Brush teeth night',
    stars: 1,
    completed: false,
    daily: true,
    order: 2
  },
  {
    id: '4',
    title: '👕 Change clothes',
    stars: 1,
    completed: false,
    daily: true,
    order: 3
  },
  {
    id: '5',
    title: '📚 Read a book',
    stars: 3,
    completed: false,
    daily: true,
    order: 4
  },
  {
    id: '6',
    title: '❤️ Be kind',
    stars: 1,
    completed: false,
    daily: false,
    resetInterval: 60, // Resets every hour
    order: 5
  }
];

const defaultRewards: Reward[] = [
  {
    id: '1',
    title: '📱 Extra screen time',
    description: '30 minutes',
    cost: 15,
    claimed: false,
    type: 'fixed'
  },
  {
    id: '2',
    title: '🍦 Special dessert',
    cost: 10,
    claimed: false,
    type: 'fixed'
  },
  {
    id: '3',
    title: '🌳 Trip to the park',
    cost: 20,
    claimed: false,
    type: 'fixed'
  },
  {
    id: '4',
    title: '🍽️ Pick dinner menu',
    description: 'Choose what to eat for dinner',
    cost: 15,
    claimed: false,
    type: 'fixed'
  },
  {
    id: '5',
    title: '💰 $10 for bank account',
    description: 'Save for the future',
    cost: 50,
    claimed: false,
    type: 'fixed'
  }
];

// Helper to get today's date as a string
export const getTodayString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Default data
export const defaultData: AppData = {
  tasks: defaultTasks,
  rewards: defaultRewards,
  stars: 0,
  theme: 'robots',
  streakDays: 0,
  achievements: defaultAchievements,
  lastLogin: getTodayString(),
  lastAccessDate: getTodayString(),
  starHistory: [],
  totalStarsEarned: 0,
  walletSettings: {
    enabled: false,
    conversionRate: 10 // 10 stars = 1 currency unit
  },
  walletData: {
    currentBalance: 0,
    lifetimeEarned: 0
  },
  bottomNavigation: false, // Enable bottom tab navigation instead of top tabs
  wishListEnabled: false, // Default disabled for new installations
  wishList: [], // Empty wish list by default
  tabSettings: {
    tasksEnabled: true, // Tasks should always be enabled by default (core functionality)
    rewardsEnabled: true, // Rewards tab enabled by default
    trophiesEnabled: true, // Trophies tab enabled by default
    wishListEnabled: false, // Wish list disabled by default
    walletEnabled: true, // Wallet tab enabled by default (if wallet feature is enabled)
    moneyTrackingEnabled: false, // Money tracking tab disabled by default (deprecated - merged into wallet)
    allowAddMoney: false // Allow adding money sources from main page disabled by default
  },
  moneyTracking: [], // Empty money tracking list by default
  // Family sharing fields (added later, defaults to undefined for non-family users)
  familyId: undefined,
  familyMetadata: undefined,
  familyMembers: undefined,
  familyInvites: undefined,
  activityLog: [],
  lastLogCleanup: undefined
};

// Activity log helpers
export const createActivityLogEntry = (
  userId: string,
  userName: string,
  action: ActivityLogEntry['action'],
  details: ActivityLogEntry['details']
): ActivityLogEntry => {
  return {
    id: `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    userId,
    userName,
    action,
    timestamp: new Date().toISOString(),
    details
  };
};

export const addActivityLogEntry = (data: AppData, entry: ActivityLogEntry): AppData => {
  const activityLog = data.activityLog || [];
  return {
    ...data,
    activityLog: [...activityLog, entry]
  };
};

// Cleanup activity log to keep only the last 1000 entries
export const cleanupActivityLog = (data: AppData): AppData => {
  if (!data.activityLog || data.activityLog.length <= 1000) {
    return data;
  }

  console.log(`[cleanupActivityLog] Cleaning up activity log. Current entries: ${data.activityLog.length}, keeping last 1000`);

  return {
    ...data,
    activityLog: data.activityLog.slice(-1000), // Keep newest 1000 entries
    lastLogCleanup: new Date().toISOString()
  };
};

/**
 * Migrate old data to ensure it has all required fields
 * This prevents errors when loading data that was created before family sharing features were added
 */
export const migrateDataSchema = (input: unknown): AppData => {
  try {
    console.log('[migrateDataSchema] Checking data schema...');

    const data = (input ?? {}) as Record<string, unknown>;

    // Ensure activityLog exists and is a valid array
    if (!Array.isArray(data.activityLog)) {
      console.log('[migrateDataSchema] Adding missing activityLog field');
      data.activityLog = [];
    } else {
      // Validate and clean activityLog entries
      const originalLength = (data.activityLog as unknown[]).length;
      data.activityLog = (data.activityLog as unknown[]).filter((entry: unknown) => {
        try {
          // Validate required fields
          if (!entry || typeof entry !== 'object') return false;
          const e = entry as Record<string, unknown>;
          if (typeof e.id !== 'string') return false;
          if (typeof e.userId !== 'string') return false;
          if (typeof e.userName !== 'string') return false;
          if (typeof e.action !== 'string') return false;
          if (typeof e.timestamp !== 'string') return false;
          // Validate timestamp is a valid date
          if (isNaN(new Date(e.timestamp).getTime())) return false;
          // Ensure details is an object or undefined
          if (e.details !== undefined && typeof e.details !== 'object') return false;
          return true;
        } catch (error) {
          console.warn('[migrateDataSchema] Removing invalid activityLog entry:', error);
          return false;
        }
      });
      if (originalLength !== (data.activityLog as unknown[]).length) {
        console.log(`[migrateDataSchema] Cleaned activityLog: ${originalLength} → ${(data.activityLog as unknown[]).length} valid entries`);
      }
    }

    // Ensure all optional family fields exist (can be undefined)
    if (data.familyId === undefined) {
      data.familyId = undefined;
    }
    if (data.familyMetadata === undefined) {
      data.familyMetadata = undefined;
    }
    if (data.familyMembers === undefined) {
      data.familyMembers = undefined;
    }
    if (data.familyInvites === undefined) {
      data.familyInvites = undefined;
    }
    if (data.lastLogCleanup === undefined) {
      data.lastLogCleanup = undefined;
    }

    // Ensure other potentially missing fields have defaults
    if (!data.starHistory) {
      data.starHistory = [];
    }
    if (!data.totalStarsEarned) {
      data.totalStarsEarned = 0;
    }
    if (!data.walletData) {
      data.walletData = { currentBalance: 0, lifetimeEarned: 0 };
    }
    if (!data.walletSettings) {
      data.walletSettings = { enabled: false, conversionRate: 10 };
    }
    if (!data.wishList) {
      data.wishList = [];
    }
    if (!data.tabSettings) {
      data.tabSettings = {
        tasksEnabled: true,
        rewardsEnabled: true,
        trophiesEnabled: true,
        wishListEnabled: false,
        walletEnabled: true,
        moneyTrackingEnabled: false,
        allowAddMoney: false
      };
    }

    // Ensure moneyTracking array exists
    if (!data.moneyTracking) {
      data.moneyTracking = [];
    }

    // Ensure moneyTrackingEnabled exists in tabSettings
    if (data.tabSettings && (data.tabSettings as Record<string, unknown>).moneyTrackingEnabled === undefined) {
      (data.tabSettings as Record<string, unknown>).moneyTrackingEnabled = false;
    }

    console.log('[migrateDataSchema] Data schema migration complete');
    return data as unknown as AppData;
  } catch (error) {
    console.error('[migrateDataSchema] Error during migration:', error);
    // If migration fails, return default data to prevent app crashes
    console.warn('[migrateDataSchema] Migration failed, returning default data');
    return defaultData;
  }
};

// Check and reset for a new day - centralized day-change logic
export const checkAndResetForNewDay = (data: AppData): AppData => {
  const today = getTodayString();

  // Initialize lastAccessDate and lastLogin if missing (backwards compatibility)
  if (!data.lastAccessDate) {
    data = {
      ...data,
      lastAccessDate: data.lastLogin || today
    };
  }

  if (!data.lastLogin) {
    data = {
      ...data,
      lastLogin: today
    };
  }

  // Check if it's a new day
  if (data.lastAccessDate === today) {
    // Same day, no reset needed
    return data;
  }

  console.log(`[checkAndResetForNewDay] New day detected. Last access: ${data.lastAccessDate}, Today: ${today}`);

  // Reset daily tasks (preserve non-daily task state)
  const updatedTasks = data.tasks.map(task => {
    if (task.daily) {
      return { ...task, completed: false, completedDate: undefined };
    }
    return task;
  });

  // Reset daily rewards using existing logic
  const updatedRewards = data.rewards.map(reward => {
    if (reward.daily && reward.lastResetDate !== today) {
      return {
        ...reward,
        claimed: false,
        claimedOn: undefined,
        lastResetDate: today
      };
    }
    // Also reset non-daily rewards that were claimed (maintain existing behavior)
    if (!reward.daily) {
      return {
        ...reward,
        claimed: false,
        claimedOn: undefined
      };
    }
    return reward;
  });

  // Update streak logic (migrated from loadData)
  let streakDays = data.streakDays;
  if (data.lastLogin) {
    // Parse dates as YYYY-MM-DD (ignoring time component)
    const lastLoginDate = new Date(data.lastLogin + 'T00:00:00');
    const todayDate = new Date(today + 'T00:00:00');
    const diffTime = Math.abs(todayDate.getTime() - lastLoginDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      streakDays += 1;
    } else if (diffDays > 1) {
      streakDays = 0;
    }
  }

  // Clean up activity log during daily reset
  let updatedData: AppData = {
    ...data,
    tasks: updatedTasks,
    rewards: updatedRewards,
    streakDays,
    lastLogin: today,
    lastAccessDate: today
  };

  // Perform activity log cleanup
  updatedData = cleanupActivityLog(updatedData);

  // Return updated data with new date stamps
  return updatedData;
};

// Helper to get current timestamp
export const getCurrentTimestamp = (): string => {
  return new Date().toISOString();
};

// Calculate the value of a calculated reward based on total stars earned
export const calculateRewardValue = (reward: Reward, totalStarsEarned: number): number => {
  if (reward.type !== 'calculated' || !reward.rate || !reward.threshold) {
    return 0;
  }
  
  const completedThresholds = Math.floor(totalStarsEarned / reward.threshold);
  return completedThresholds * reward.rate;
};

// Calculate daily task statistics
export const calculateDailyTaskStats = (tasks: Task[], today: string) => {
  const dailyTasks = tasks.filter(task => task.daily);
  const completedDailyTasks = dailyTasks.filter(task => task.completed);
  const totalDailyStars = dailyTasks.reduce((sum, task) => sum + task.stars, 0);
  const completedDailyStars = completedDailyTasks.reduce((sum, task) => sum + task.stars, 0);
  
  return {
    totalTasks: dailyTasks.length,
    tasksCompleted: completedDailyTasks.length,
    maxPossibleStars: totalDailyStars,
    starsEarned: completedDailyStars,
    isPerfectDay: totalDailyStars > 0 && completedDailyStars === totalDailyStars
  };
};

// Update star history tracking
export const updateStarHistory = (data: AppData, newStars: number): AppData => {
  const today = getTodayString();
  const newTotalStarsEarned = (data.totalStarsEarned || 0);
  
  // Calculate daily task statistics
  const dailyStats = calculateDailyTaskStats(data.tasks, today);
  
  // Initialize starHistory if it doesn't exist
  const starHistory = data.starHistory || [];
  
  // Check if we already have an entry for today
  const todayEntryIndex = starHistory.findIndex(entry => entry.date === today);
  
  if (todayEntryIndex >= 0) {
    // Update today's entry
    const updatedHistory = [...starHistory];
    updatedHistory[todayEntryIndex] = {
      ...updatedHistory[todayEntryIndex],
      stars: newStars,
      totalStarsEarned: newTotalStarsEarned,
      maxPossibleStars: dailyStats.maxPossibleStars,
      tasksCompleted: dailyStats.tasksCompleted,
      totalTasks: dailyStats.totalTasks,
      isPerfectDay: dailyStats.isPerfectDay
    };
    
    return {
      ...data,
      starHistory: updatedHistory
    };
  } else {
    // Add new entry for today
    const newEntry: StarHistoryEntry = {
      date: today,
      stars: newStars,
      totalStarsEarned: newTotalStarsEarned,
      maxPossibleStars: dailyStats.maxPossibleStars,
      tasksCompleted: dailyStats.tasksCompleted,
      totalTasks: dailyStats.totalTasks,
      isPerfectDay: dailyStats.isPerfectDay
    };
    
    // Keep only last 30 days of history to avoid unlimited growth
    const updatedHistory = [...starHistory, newEntry].slice(-30);
    
    return {
      ...data,
      starHistory: updatedHistory
    };
  }
};

// Check if a task needs to be reset based on its reset interval
export const shouldResetTask = (task: Task): boolean => {
  if (!task.resetInterval || !task.lastReset) return false;
  
  const now = new Date();
  const lastReset = new Date(task.lastReset);
  const diffMs = now.getTime() - lastReset.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  
  return diffMinutes >= task.resetInterval;
};

// Calculate remaining minutes until a task resets
export const getTimeUntilReset = (task: Task): number => {
  if (!task.resetInterval || !task.lastReset) return 0;
  
  const now = new Date();
  const lastReset = new Date(task.lastReset);
  const diffMs = now.getTime() - lastReset.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  const remainingMinutes = task.resetInterval - diffMinutes;
  
  return Math.max(0, Math.ceil(remainingMinutes));
};

// Format remaining time in a human-readable way
export const formatTimeRemaining = (minutes: number): string => {
  if (minutes <= 0) return 'Ready to reset';
  
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  if (minutes < 1440) { // less than 24 hours
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    
    if (remainingMins === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    
    return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMins} minute${remainingMins !== 1 ? 's' : ''}`;
  }
  
  // 24 hours or more
  const days = Math.floor(minutes / 1440);
  const remainingHours = Math.floor((minutes % 1440) / 60);
  
  if (remainingHours === 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  
  return `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
};

// Check achievements and unlock any that have been earned
export const checkAndUnlockAchievements = (data: AppData): AppData => {
  let achievementsChanged = false;
  const newlyUnlockedAchievements = [];
  let firstUnlockedAchievementTitle = '';
  
  const updatedAchievements = data.achievements.map(achievement => {
    // Skip already unlocked achievements
    if (achievement.unlocked) {
      return achievement;
    }
    
    // Check star-based achievements
    if (!achievement.linkedTaskId && achievement.requiredStars && data.stars >= achievement.requiredStars) {
      achievementsChanged = true;
      newlyUnlockedAchievements.push(achievement.id);
      
      // Store the title of the first achievement we unlock
      if (firstUnlockedAchievementTitle === '') {
        firstUnlockedAchievementTitle = achievement.title;
      }
      
      return {
        ...achievement,
        unlocked: true,
        date: new Date().toISOString()
      };
    }
    
    // Check task-based achievements
    if (achievement.linkedTaskId && achievement.requiredCompletions) {
      const linkedTask = data.tasks.find(task => task.id === achievement.linkedTaskId);
      if (linkedTask && linkedTask.completionCount && linkedTask.completionCount >= achievement.requiredCompletions) {
        achievementsChanged = true;
        newlyUnlockedAchievements.push(achievement.id);
        
        // Store the title of the first achievement we unlock
        if (firstUnlockedAchievementTitle === '') {
          firstUnlockedAchievementTitle = achievement.title;
        }
        
        return {
          ...achievement,
          unlocked: true,
          date: new Date().toISOString()
        };
      }
    }
    
    return achievement;
  });
  
  if (achievementsChanged) {
    const newData = {
      ...data,
      achievements: updatedAchievements
    };
    
    // Import here to avoid circular dependency
    import('./animations').then(animations => {
      // Only trigger animation for the first newly unlocked achievement
      if (newlyUnlockedAchievements.length > 0) {
        animations.celebrateAchievement(firstUnlockedAchievementTitle);
      }
    });
    
    return newData;
  }
  
  return data;
};

// Reset data
export const resetData = (): AppData => {
  const today = getTodayString();
  const freshData = {
    ...defaultData,
    lastLogin: today,
    lastAccessDate: today,
    starHistory: [],
    totalStarsEarned: 0
  };
  return freshData;
};

// Reset daily tasks
export const resetDailyTasks = (data: AppData): AppData => {
  const updatedTasks = data.tasks.map(task => {
    if (task.daily) {
      return { ...task, completed: false, completedDate: undefined };
    }
    return task;
  });
  
  // Also reset all claimed rewards
  const updatedRewards = data.rewards.map(reward => {
    return {
      ...reward,
      claimed: false,
      claimedOn: undefined
    };
  });
  
  const newData = {
    ...data,
    tasks: updatedTasks,
    rewards: updatedRewards
  };
  
  return newData;
};

// Reset only daily rewards at midnight
export const resetDailyRewards = (data: AppData): AppData => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  
  const updatedRewards = data.rewards.map(reward => {
    // Reset daily rewards that haven't been reset today
    if (reward.daily && reward.lastResetDate !== today) {
      return {
        ...reward,
        claimed: false,
        claimedOn: undefined,
        lastResetDate: today
      };
    }
    return reward;
  });
  
  return {
    ...data,
    rewards: updatedRewards
  };
};

// Check if daily rewards need to be reset (call this every 15 minutes and on page load)
export const checkAndResetDailyRewards = (data: AppData): AppData => {
  const now = new Date();
  const today = now.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Check if any daily rewards need to be reset
  const needsReset = data.rewards.some(reward => 
    reward.daily && reward.lastResetDate !== today
  );
  
  if (needsReset) {
    console.log('Resetting daily rewards for new day:', today);
    return resetDailyRewards(data);
  }
  
  return data;
};

// Reset interval-based tasks if needed
export const checkAndResetTimedTasks = (data: AppData): AppData => {
  const tasksToReset = data.tasks.filter(task => 
    task.resetInterval && shouldResetTask(task)
  );
  
  if (tasksToReset.length > 0) {
    console.log(`[checkAndResetTimedTasks] Resetting ${tasksToReset.length} interval-based task(s) on page load`);
  }
  
  const updatedTasks = data.tasks.map(task => {
    if (task.resetInterval && shouldResetTask(task)) {
      return { 
        ...task, 
        completed: false, 
        lastReset: getCurrentTimestamp() 
      };
    }
    return task;
  });
  
  if (JSON.stringify(updatedTasks) !== JSON.stringify(data.tasks)) {
    const newData = {
      ...data,
      tasks: updatedTasks
    };
    
    return newData;
  }
  
  return data;
};

// Helper to check if a task is linked to an achievement
export const isTaskLinkedToAchievement = (taskId: string, achievements: Achievement[]): boolean => {
  return achievements.some(achievement => achievement.linkedTaskId === taskId);
};

// Update a task's completion count when completed
export const updateTaskCompletionCount = (task: Task): Task => {
  return {
    ...task,
    completionCount: (task.completionCount || 0) + 1
  };
};

// Calculate wallet balance based on total stars earned and conversion rate
export const calculateWalletBalance = (totalStarsEarned: number, conversionRate: number): number => {
  return totalStarsEarned / conversionRate;
};

// Get the effective total stars for wallet calculation (handles manual additions)
export const getEffectiveTotalStars = (data: AppData): number => {
  const totalStarsEarned = data.totalStarsEarned || 0;
  const currentStars = data.stars || 0;
  
  // Use the maximum to ensure manual star additions through Firebase/admin UI are captured
  return Math.max(totalStarsEarned, currentStars);
};

// Update wallet data when stars are earned
export const updateWalletData = (data: AppData, starsEarned: number): AppData => {
  if (!data.walletSettings?.enabled || !data.walletData) {
    return data;
  }

  const { conversionRate } = data.walletSettings;
  const newTotalStarsEarned = (data.totalStarsEarned || 0) + starsEarned;
  const increment = calculateWalletBalance(starsEarned, conversionRate);

  // Special case: if totalStarsEarned was undefined, treat this as a fresh start
  // and reset currentBalance to match the new total
  if (data.totalStarsEarned === undefined) {
    const newBalance = calculateWalletBalance(newTotalStarsEarned, conversionRate);
    return {
      ...data,
      totalStarsEarned: newTotalStarsEarned,
      walletData: {
        currentBalance: newBalance,
        lifetimeEarned: (data.walletData.lifetimeEarned || 0) + increment
      }
    };
  }

  return {
    ...data,
    totalStarsEarned: newTotalStarsEarned,
    walletData: {
      currentBalance: (data.walletData.currentBalance || 0) + increment,
      lifetimeEarned: (data.walletData.lifetimeEarned || 0) + increment
    }
  };
};

// Deduct the money-equivalent of spent stars from the spendable wallet balance.
// lifetimeEarned is intentionally left untouched (it tracks cumulative earnings).
// Because spending lowers `stars` but not `totalStarsEarned`, syncWalletBalance
// sees a zero delta afterwards and preserves this deduction rather than re-inflating it.
export const spendFromWallet = (data: AppData, starsSpent: number): AppData => {
  if (!data.walletSettings?.enabled || !data.walletData || starsSpent <= 0) {
    return data;
  }

  const moneyToDeduct = calculateWalletBalance(starsSpent, data.walletSettings.conversionRate);
  const newBalance = Math.max(0, (data.walletData.currentBalance || 0) - moneyToDeduct);

  return {
    ...data,
    walletData: {
      ...data.walletData,
      currentBalance: newBalance
    }
  };
};

// Sync wallet balance with current star count (for synchronization)
export const syncWalletBalance = (data: AppData): AppData => {
  // Always reconcile totalStarsEarned regardless of wallet settings for consistency
  const effectiveTotalStars = getEffectiveTotalStars(data);
  const needsReconciliation = effectiveTotalStars !== (data.totalStarsEarned || 0);
  
  // If wallet is not enabled or wallet data is missing, just sync totalStarsEarned
  if (!data.walletSettings?.enabled || !data.walletData) {
    if (needsReconciliation) {
      return {
        ...data,
        totalStarsEarned: effectiveTotalStars
      };
    }
    return data;
  }

  const { conversionRate } = data.walletSettings;
  const deltaStars = effectiveTotalStars - (data.totalStarsEarned || 0);
  
  // Special case: if effective total stars is 0, reset balances to 0
  if (effectiveTotalStars === 0) {
    if (data.walletData.currentBalance !== 0 || data.walletData.lifetimeEarned !== 0 || needsReconciliation) {
      return {
        ...data,
        totalStarsEarned: 0,
        walletData: {
          ...data.walletData,
          currentBalance: 0,
          lifetimeEarned: 0
        }
      };
    }
    return data;
  }
  
  // If no delta, return original data (preserves object reference optimization)
  if (deltaStars === 0) {
    return data;
  }
  
  // If there's a positive delta, apply incremental update
  if (deltaStars > 0) {
    const increment = calculateWalletBalance(deltaStars, conversionRate);
    return {
      ...data,
      totalStarsEarned: effectiveTotalStars,
      walletData: {
        ...data.walletData,
        currentBalance: (data.walletData.currentBalance || 0) + increment,
        lifetimeEarned: (data.walletData.lifetimeEarned || 0) + increment
      }
    };
  }
  
  // If deltaStars is negative, just update totalStarsEarned but keep wallet balances
  // (this handles cases where current stars were manually reduced)
  return {
    ...data,
    totalStarsEarned: effectiveTotalStars
  };
};
